import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { CalcCard, CalcField, CalcRow, CalcStat, usd } from "@/components/site/CalcKit";
import { CalculatorScaffold } from "@/components/site/CalculatorScaffold";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { calcPitia, interestOnlyPayment, monthlyPayment } from "@/lib/investor-calcs";
import dscrPaymentHero from "@/assets/hero/hero-dscr-payment-fourplex.jpg";

const FAQS = [
  {
    q: "What is PITIA on an investment property?",
    a: "PITIA is principal, interest, taxes, insurance, and association dues. On investment property it usually also includes flood insurance where required. It is the payment figure a DSCR lender divides rent by.",
  },
  {
    q: "Is the DSCR payment different from a primary residence payment?",
    a: "The arithmetic is the same, but investor loans carry different pricing, and taxes and insurance are frequently higher because the property is non-owner-occupied and, in some states, reassessed at the sale price.",
  },
  {
    q: "Do DSCR loans include mortgage insurance?",
    a: "Generally no. Most business-purpose DSCR programs do not require borrower-paid mortgage insurance and control leverage through the LTV cap instead, but requirements vary by lender and program, so confirm it for the specific loan you are quoted.",
  },
  {
    q: "How much does an interest-only payment save each month?",
    a: "It removes the principal portion of the payment. Early in a 30-year term that is typically 10–20% of the P&I, and the savings shrink as the rate rises. The balance does not amortize during the interest-only period.",
  },
  {
    q: "Should I escrow taxes and insurance?",
    a: "Many investor programs allow escrow waivers at lower leverage, sometimes with a small pricing adjustment. Whether escrowed or not, the full monthly cost still counts in DSCR.",
  },
  {
    q: "Does DSCR payment include flood insurance?",
    a: "When the property sits in a FEMA-designated flood zone, yes — flood premium is added into PITIA alongside taxes and hazard insurance. Skipping it will understate the payment and overstate DSCR, so confirm flood zone status with a determination before quoting a deal.",
  },
  {
    q: "How does HOA affect my DSCR?",
    a: "HOA dues are added directly into PITIA, so a $300/month HOA lowers DSCR the same way a $300 increase in principal and interest would. Condo and townhome deals often have meaningfully higher dues than single-family, which is why they can qualify differently at the same rent.",
  },
  {
    q: "What property tax figure should I use for a purchase?",
    a: "Use the taxing authority's projected post-sale assessment where available, since many counties reassess at the new purchase price rather than carrying over the seller's lower basis. Using the seller's old tax bill on a purchase calculation is a common way investors underestimate PITIA.",
  },
  {
    q: "Why is my estimated payment higher than a rate quote implied?",
    a: "A rate quote usually covers only principal and interest, while PITIA layers in taxes, insurance, HOA, and flood coverage on top. Two properties at the identical loan amount and rate can carry very different total payments once those escrow-type costs are included.",
  },
  {
    q: "Does a higher DSCR payment always mean a worse deal?",
    a: "Not necessarily — a higher payment paired with proportionally higher rent can still produce a strong DSCR and cash flow. What matters is the ratio of rent to PITIA, not the payment's dollar size in isolation.",
  },
  {
    q: "Can I lower PITIA by shopping insurance?",
    a: "Yes, hazard insurance premiums on investment property can vary significantly between carriers, and since insurance is a direct line item in PITIA, a lower premium immediately improves DSCR. It is worth getting more than one landlord policy quote before locking numbers into an application.",
  },
  {
    q: "Is the DSCR payment estimate here binding?",
    a: "No, every figure produced is an illustrative estimate for screening purposes only. Actual payment, escrow amounts, and DSCR depend on the appraisal, insurance binder, tax certification, and full underwriting of your specific file.",
  },
];

export const Route = createFileRoute("/dscr-payment-calculator")({
  head: () =>
    buildHead({
      title: "DSCR Payment Calculator (PITIA) | Simply Approved",
      description:
        "Estimate the full monthly payment on an investment property loan: principal, interest, taxes, insurance, flood and HOA — plus the DSCR that payment produces.",
      url: "/dscr-payment-calculator",
      keywords:
        "DSCR payment calculator, investment property mortgage payment calculator, PITIA calculator, rental property payment calculator, interest only investment property payment, DSCR monthly payment, investor loan payment estimator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Payment Calculator", url: "/dscr-payment-calculator" },
      ],
      service: { name: "DSCR Payment Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "DSCR Payment Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/dscr-payment-calculator",
          description: "Full PITIA payment estimator for business-purpose investment property loans.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: PaymentPage,
});

type Inputs = {
  price: number;
  downPct: number;
  ratePct: number;
  termYears: number;
  ioToggle: number;
  taxesAnnual: number;
  insAnnual: number;
  floodAnnual: number;
  hoaMonthly: number;
  monthlyRent: number;
};

const DEFAULT: Inputs = {
  price: 400000,
  downPct: 25,
  ratePct: 7.5,
  termYears: 30,
  ioToggle: 0,
  taxesAnnual: 4400,
  insAnnual: 2000,
  floodAnnual: 0,
  hoaMonthly: 0,
  monthlyRent: 3100,
};

function PaymentPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("dscr_payment", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("dscr_payment", String(k), v as never);
  };

  const m = useMemo(() => {
    const down = i.price * (i.downPct / 100);
    const loan = Math.max(0, i.price - down);
    const p = calcPitia({
      loanAmount: loan,
      ratePct: i.ratePct,
      termYears: i.termYears,
      interestOnly: i.ioToggle >= 1,
      taxesAnnual: i.taxesAnnual,
      insuranceAnnual: i.insAnnual,
      floodAnnual: i.floodAnnual,
      hoaMonthly: i.hoaMonthly,
    });
    const amort = monthlyPayment(loan, i.ratePct, i.termYears);
    const io = interestOnlyPayment(loan, i.ratePct);
    const dscr = p.pitia > 0 ? i.monthlyRent / p.pitia : 0;
    return { down, loan, p, amort, io, dscr, ltv: i.price > 0 ? loan / i.price : 0 };
  }, [i]);

  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackCalcEstimateViewed("dscr_payment", { pitia: m.p.pitia, dscr: Number(m.dscr.toFixed(2)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Purchase price", value: usd(i.price) },
    { label: "Loan amount", value: usd(m.loan) },
    { label: "LTV", value: `${(m.ltv * 100).toFixed(1)}%` },
    { label: "Monthly PITIA", value: usd(m.p.pitia) },
    { label: "Principal & interest", value: usd(m.p.pi) },
    { label: "Monthly rent", value: usd(i.monthlyRent) },
    { label: "DSCR", value: m.dscr.toFixed(2) },
  ];

  return (
    <CalculatorScaffold
      calculator="dscr_payment"
      eyebrow="Investment property payment"
      title="DSCR Payment Calculator: Full Monthly PITIA"
      subtitle="Build the complete monthly cost of an investment property loan — principal and interest, taxes, insurance, flood, and HOA — and see the DSCR it produces. Illustrative estimates only; not a quote, approval, or commitment to lend."
      shareUrl={shareUrl}
      shareTitle="My investment property payment estimate"
      heroImage={{
        src: dscrPaymentHero,
        alt: "Four-unit residential investment property used to illustrate a DSCR payment calculation",
      }}
      inputs={
        <>
          <CalcCard title="1. Price & leverage">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Purchase price / value" value={i.price} onChange={(v) => set("price", v)} prefix="$" />
              <CalcField label="Down payment" value={i.downPct} onChange={(v) => set("downPct", v)} suffix="%" step={1} hint="25% down = 75% LTV, a common DSCR purchase structure." />
            </div>
          </CalcCard>
          <CalcCard title="2. Loan terms">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Note rate" value={i.ratePct} onChange={(v) => set("ratePct", v)} suffix="%" step={0.125} hint="Scenario assumption you enter — not a quoted rate." />
              <CalcField label="Amortization (years)" value={i.termYears} onChange={(v) => set("termYears", v)} step={5} />
              <CalcField label="Interest-only? (1 = yes)" value={i.ioToggle} onChange={(v) => set("ioToggle", v)} step={1} />
            </div>
          </CalcCard>
          <CalcCard title="3. Carrying costs & rent">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Property taxes (annual)" value={i.taxesAnnual} onChange={(v) => set("taxesAnnual", v)} prefix="$" />
              <CalcField label="Hazard insurance (annual)" value={i.insAnnual} onChange={(v) => set("insAnnual", v)} prefix="$" />
              <CalcField label="Flood insurance (annual)" value={i.floodAnnual} onChange={(v) => set("floodAnnual", v)} prefix="$" />
              <CalcField label="HOA dues (monthly)" value={i.hoaMonthly} onChange={(v) => set("hoaMonthly", v)} prefix="$" />
              <CalcField label="Monthly rent" value={i.monthlyRent} onChange={(v) => set("monthlyRent", v)} prefix="$" />
            </div>
          </CalcCard>
        </>
      }
      results={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CalcStat label="Monthly PITIA" value={usd(m.p.pitia)} />
            <CalcStat label="Loan amount" value={usd(m.loan)} />
            <CalcStat label="DSCR" value={m.dscr.toFixed(2)} colorClass={m.dscr >= 1.25 ? "text-emerald-600" : m.dscr >= 1 ? "text-amber-600" : "text-destructive"} />
            <CalcStat label="LTV" value={`${(m.ltv * 100).toFixed(1)}%`} />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-foreground">Payment detail</h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              <CalcRow label="Down payment" value={usd(m.down)} />
              <CalcRow label={i.ioToggle >= 1 ? "Interest-only payment" : "Principal & interest"} value={usd(m.p.pi)} strong />
              <CalcRow label="Amortizing P&I (for comparison)" value={usd(m.amort)} />
              <CalcRow label="Interest-only (for comparison)" value={usd(m.io)} />
              <CalcRow label="Property taxes" value={usd(m.p.taxes)} />
              <CalcRow label="Hazard insurance" value={usd(m.p.insurance)} />
              <CalcRow label="Flood insurance" value={usd(m.p.flood)} />
              <CalcRow label="HOA dues" value={usd(m.p.hoa)} />
              <CalcRow label="Total PITIA" value={usd(m.p.pitia)} strong />
              <CalcRow
                label="Rent less PITIA"
                value={usd(i.monthlyRent - m.p.pitia)}
                colorClass={i.monthlyRent - m.p.pitia >= 0 ? "text-emerald-600" : "text-destructive"}
              />
            </dl>
          </div>
        </div>
      }
      aiFacts={aiFacts}
      aiSuggestions={[
        "What is included in PITIA on an investment property?",
        "How much would interest-only lower this payment?",
        "How does more down payment change the DSCR here?",
        "Why are investor taxes and insurance often higher?",
      ]}
      methodologyIntro="Everything here is standard amortization math plus the carrying costs a lender counts in the housing payment."
      methodology={
        <>
          <p>
            <strong>Principal and interest.</strong> Level payment on the entered rate and amortization. The
            interest-only option charges balance × rate ÷ 12 and leaves the balance unchanged.
          </p>
          <p>
            <strong>Taxes and insurance.</strong> Entered annually and divided by twelve. In states that reassess on
            transfer, the post-sale bill can be materially higher than what the seller pays today — model that number,
            not the current one.
          </p>
          <p>
            <strong>What is excluded.</strong> Mortgage insurance is not modeled, since most business-purpose DSCR programs do not require it (confirm for your program); also excluded are
            operating expenses such as management or maintenance, and lender fees. Cash flow modeling belongs in the{" "}
            cash-on-cash tool; closing cash belongs in the closing cost tool.
          </p>
        </>
      }
      faqs={FAQS}
      faqTitle="Investment property payment FAQ"
      related={[
        { to: "/dscr-calculator", label: "DSCR calculator" },
        { to: "/rent-needed-for-dscr", label: "Rent needed for a target DSCR" },
        { to: "/closing-cost-calculator", label: "Closing cost & cash-to-close calculator" },
        { to: "/interest-only-vs-amortizing-calculator", label: "Interest-only vs amortizing" },
        { to: "/cash-on-cash-return-calculator", label: "Cash-on-cash return calculator" },
        { to: "/investment-property-rates", label: "Price a DSCR scenario" },
      ]}
      ctaTitle="Want this payment priced properly?"
      ctaBody="Share the property details and a licensed loan officer will return an illustrative structure for business-purpose, non-owner-occupied financing."
    />
  );
}
